import type { BrainRepoWriter } from "./types.js";
import type { DreamDigest } from "./digest.js";
import { renderDigestMarkdown } from "./digest.js";

export interface DigestCommitResult {
  committed: boolean;
  newSha?: string;
  /** Set when the write/verify failed — the digest payload, handed back for re-emit / log (never lost). */
  reconstructible?: DreamDigest;
  reason?: "non-fast-forward" | "error" | "verify-failed";
}

const DIGEST_DIR = "artifacts/dream-digest";

function digestPaths(cycle: string): { json: string; md: string } {
  return { json: `${DIGEST_DIR}/${cycle}.json`, md: `${DIGEST_DIR}/${cycle}.md` };
}

/**
 * Commit the digest as its OWN commit, AFTER the delta batch + that batch's end-state verify
 * (DESIGN §4.4, §9). Keyed by the cursor-window upper bound `<cycle>` so a rebase-retry re-writes
 * the SAME pair idempotently. Append-only: never reverted on its own (the revert drill targets the
 * (delta, digest) pair). On any failure the assembled digest is returned as `reconstructible` so
 * the caller can re-emit / log it — the digest is never silently lost.
 *
 * BEST-EFFORT / NON-FATAL: this runs AFTER the deltas + cursor advance have already landed, so it
 * must NEVER reject. `fetchTip()` and `readFile()` THROW on non-ok HTTP / token-mint / network
 * (see writer.ts), so every leg is wrapped — a thrown error is converted to the same
 * `{ committed: false, reason: "error" }` disposition `commitBatch`'s `{ok:false}` RETURN takes,
 * so the caller's non-fatal handling covers throw and return alike (DESIGN §4.4, §9).
 */
export async function commitDreamDigest(
  digest: DreamDigest,
  brain: BrainRepoWriter,
  deps: { fetchTip: () => Promise<string> },
): Promise<DigestCommitResult> {
  const { json, md } = digestPaths(digest.cycle);
  const jsonContent = JSON.stringify(digest, null, 2) + "\n";
  const mdContent = renderDigestMarkdown(digest);

  try {
    const expectedOldSha = await deps.fetchTip();
    const res = await brain.commitBatch({
      expectedOldSha,
      message: `dream digest: ${digest.worker} ${digest.cycle}`,
      changes: [
        { path: json, content: jsonContent },
        { path: md, content: mdContent },
      ],
    });

    if (!res.ok) {
      // non-fast-forward or error: hand the payload back, re-emittable on the next cycle.
      return { committed: false, reason: res.reason ?? "error", reconstructible: digest };
    }

    // End-state verify by repo END STATE (read the json blob back), never commit count (DESIGN §4.4).
    const landed = await brain.readFile(json);
    if (landed === null || landed !== jsonContent) {
      return { committed: false, reason: "verify-failed", reconstructible: digest };
    }

    return { committed: true, newSha: res.newSha };
  } catch {
    // fetchTip()/readFile() throw on non-ok HTTP / token / network — collapse to the SAME
    // non-fatal disposition as a {ok:false} return so the digest leg is truly best-effort.
    return { committed: false, reason: "error", reconstructible: digest };
  }
}
